<template>

<div class="main list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList(1)" v-focus>
			<div class="button action input-go" @click="getList(1)"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters" v-if="showFilters">
		<label>Cart Type</label>
		<select v-model="filters.shopping_cart_type_id" @change="toggleFilter('shopping_cart_type')">
			<option value="">Select...</option>
			<option v-for="(shoppingCartType, index) in shoppingCartTypes" :value="shoppingCartType.shopping_cart_type_id">{{ shoppingCartType.description }}</option>
		</select>
		<div v-if="orderTypeDescription" class="note" style="margin-left: 0;">
			Looking for completed {{ orderTypeDescription }}s?
			<div class="button small neutral" @click="goToOrders()">Go To Orders</div>
		</div>
		<div class="label-group">
			<label>Start</label>
			<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label>End</label>
			<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label></label>
			<div class="button small neutral" @click="getList(1)">Filter Dates</div>
		</div>
		<div class="divider"></div>
		<ul class="checkbox-group">
			<li>
				<span :class="['pointer', 'far', !filters.empty_carts ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('empty_carts')"></span>
				<label>Exclude empty carts</label>
			</li>
			<li>
				<span :class="['pointer', 'far', filters.customer_carts ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('customer_carts')"></span>
				<label>Include customer carts</label>
			</li>
			<li>
				<span :class="['pointer', 'far', filters.sort_descending ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('sort_descending')"></span>
				<label>Sort oldest to newest</label>
			</li>
		</ul>
		<button :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="clearFilters(1)">Clear Filters</button>
		<div class="divider"></div>
		<div class="button action" @click="createCart()">Create Cart</div>
		<div v-if="showDetail" class="button neutral" @click="backToList()">Back To List</div>
		<div class="divider"></div>

		<div v-if="cartDetail.shopping_cart_id" class="extra-menu-item" @click="printCart('store')">Print {{ cartDetail.shopping_cart_type }}</div>
		<div v-if="cartDetail.shopping_cart_id" class="extra-menu-item" @click="printCart('customer')">Print Customer Copy</div>
		<div v-if="cartDetail.shopping_cart_id" class="extra-menu-item" @click="confirmDeleteCart = true">Delete {{ cartDetail.shopping_cart_type }}</div>

		<div v-if="!showDetail" :class="['extra-menu-item', { disabled: gettingExport }]" @click="doExport()">Export List</div>
		<div v-if="!showDetail" class="extra-menu-item" @click="openTermsModal()">Terms &amp; Conditions</div>
		<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList(1)">Reload Report</div>

	</div>

	<div v-if="!showDetail" class="list">
	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>
	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Id</th>
					<th class="list">Date</th>
					<th class="list">Type</th>
					<th class="list">Customer</th>
					<th class="list">Status</th>
					<th class="list right">Total</th>
				</tr>
			</thead>
			<tr v-for="(cart, index) in list" class="clickable-list-item" @click="getItemRoute(cart.shopping_cart_id)">
				<td class="list">{{ cart.shopping_cart_id }}</td>
				<td class="list">{{ cart.date_created }}</td>
				<td class="list">{{ cart.shopping_cart_type }}</td>
				<td class="list">
					{{ cart.full_name || cart.company_name }}
					<div v-if="cart.address_1">
						{{ cart.address_1 }} {{ cart.city }} {{ cart.state }} {{ cart.zip_code }}
					</div>
				</td>
				<td class="list">{{ cart.shopping_cart_status }}</td>
				<td class="list right">{{ cart.total }}</td>
			</tr>
		</table>
	</div>

	<div v-if="showDetail" class="detail">
		<cart-detail :cartDetail="cartDetail" :shoppingCartTypes="shoppingCartTypes" :shoppingCartStatusCodes="shoppingCartStatusCodes" :showPrint="showPrint" :key="cartDetailHash" @update="getItem" />
	</div>

	<div class="modal" v-if="showCreateModal">
		<div class="inner">
			<h2>Create a New Cart</h2>
			<div class="content">
				<label-required label="Cart Type" :condition="cartDetail.shopping_cart_type_id" />
				<select v-model="cartDetail.shopping_cart_type_id">
					<option value="">Select...</option>
					<option v-for="(shoppingCartType, index) in shoppingCartTypes" :value="shoppingCartType.shopping_cart_type_id">{{ shoppingCartType.description }}</option>
				</select>

				<label-required label="Customer" :condition="cartDetail.contact_id" />
				<div class="three-one-columns">
					<div class="input-with-control">
						<input type="text" ref="search_term" v-model="cartDetail.billing.full_name" placeholder="Search..." @keyup.enter="searchCustomers()">
						<div class="button action input-go" @click="searchCustomers()"><i class="fas fa-search"></i></div>
					</div>
					<button class="action" @click="newCustomer()">New</button>
				</div>

				<div v-if="customerSearchMessage" class="message">{{ customerSearchMessage }}</div>
				<div v-if="this.cartDetail.contact_id">
					{{ this.cartDetail.billing.full_name }}<br>
					{{ this.cartDetail.billing.address_1 }} {{ this.cartDetail.billing.city }} {{ this.cartDetail.billing.state }} {{ this.cartDetail.billing.zip_code }}
				</div>
				<ul v-if="customerList.length > 0" class="search-list">
					<li v-for="(customer, index) in customerList" class="clickable-list-item" @click="assignCustomer(customer)">
						{{ customer.full_name }}<br>
						{{ customer.address_1 }} {{ customer.city }} {{ customer.state }} {{ customer.zip_code }}
					</li>
				</ul>

				<div v-if="saveMessage" class="message">{{ saveMessage }}</div>

			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showCreateModal = false">Cancel</div>
				<div :class="['button', cartDetail.shopping_cart_type_id && cartDetail.contact_id ? 'action' : 'disabled', 'spaced']" @click="saveCart()">Create Cart</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="confirmDeleteCart">
		<div class="inner">
			<h2>Confirm Delete {{ cartDetail.shopping_cart_type }}</h2>
			<div class="content">
				<p v-if="cartDetail.payments.length > 0">
					This {{ cartDetail.shopping_cart_type }} has payment(s) attached.  You'll need to refund the payments in POS before deleting.
				</p>
				<p v-else>
					Are you sure you want to delete this {{ cartDetail.shopping_cart_type }}?
				</p>
				<p v-if="saveMessage">{{ saveMessage }}</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="confirmDeleteCart = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'delete']" @click="deleteCart()">Yes, delete</div>
			</div>
		</div>
	</div>

	<div class="modal large-modal" v-if="showContactModal">
		<div class="inner">
			<h2>Create a New Customer</h2>
			<div class="content">
				<contact-detail-form :clientId="clientId" :contact="editedBilling" :hideControls="true" @update="setEditedBilling" />
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showContactModal = false">Cancel</div>
				<div :class="['button', customerInfoComlete ? 'action' : 'disabled', 'spaced']" @click="saveCustomer()">Save Customer</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showTermsModal">
		<div class="inner">
			<h2>Terms and Conditions</h2>
			<div class="content">
				<textarea v-model="terms" class="terms-textarea" cols="30" rows="50"></textarea>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showTermsModal = false">Cancel</div>
				<div class="button action" @click="saveTerms()">Save Terms</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{342:'Work Orders & Carts'}" :tours="false"></help-popup>

</div>

</template>

<script>
import LabelRequired from '@/components/LabelRequired'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import CartDetail from '@/components/CartDetail'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'cart-list',
	props: ['shopping_cart_id'],
    components: {
		'label-required': LabelRequired,
        'contact-detail-form': ContactDetailForm,
        'cart-detail': CartDetail,
		'help-popup': HelpPopup
	},
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			clientId: '',
			shoppingCartTypes: [],
			shoppingCartStatusCodes: [],
			showFilters: true,
			filters: {},
            default_start_date: '',
            default_end_date: '',
			filterMessage: '',
			filterLoading: false,
			list: [],
			paging: {},
			showDetail: false,
			cartDetail: {},
			exportMessage: '',
			gettingExport: false,
			saving: false,
			sendingEmails: false,
			showPrint: false,
			showCreateModal: false,
			showContactModal: false,
			editedBilling: {},
			searching: false,
			customerList: [],
			customerSearchMessage: '',
			saving: false,
			saveMessage: '',
			confirmDeleteCart: false,
			terms: '',
			showTermsModal: false,
		}
	},
	created() {
		this.getCartTypes()
		if (this.shopping_cart_id) {
			this.getItem(this.shopping_cart_id)
		} else {
			this.clearFilters(true) // will get list
		}
	},
	computed: {
		hasFilters() {
			return this.filters.search_term || this.filters.shopping_cart_type_id || this.filters.start_date != this.default_start_date || this.filters.end_date != this.default_end_date || !this.filters.empty_carts
		},
		orderTypeDescription() {
			if (!this.filters.shopping_cart_type_id) { return false }
			let type = this.shoppingCartTypes.find(type => type.shopping_cart_type_id == this.filters.shopping_cart_type_id)
			switch (type.description) {
				case 'Gunsmithing':
					return 'Gunsmithing Order'
				case 'Quote':
					return 'Quote'
				case 'Work Order':
					return 'Work Order'
				default:
					return false
			}

		},
		cartDetailHash() {
			let hash = this.cartDetail.contact_id
			if (this.cartDetail.items) {
				hash += '_' + Object.keys(this.cartDetail.items).length
			}
			if (this.cartDetail.fees) {
				hash += '_' + this.cartDetail.fees.length
			}
			if (this.cartDetail.discounts) {
				hash += '_' + this.cartDetail.discounts.length
			}
			hash += '_' + this.showPrint
			return hash
		},
		customerInfoComlete() {
			return ((this.editedBilling.first_name && this.editedBilling.last_name) || this.editedBilling.company_name)
					&& this.editedBilling.address_1 && this.editedBilling.city && this.editedBilling.state && this.editedBilling.zip_code
		}
	},
	watch: {
		'$route.path': function() {
			if (this.shopping_cart_id) {
				this.getItem(this.shopping_cart_id)
			} else {
				this.getList() // will get list
			}
		}
	},
	methods: {
		getCartTypes() {
			this.apiCall({ destination: 'get_list_shopping_cart_types' }).then(obj => {
				this.shoppingCartTypes = obj.shopping_cart_types
				this.shoppingCartStatusCodes = obj.shopping_cart_status_codes
			}).catch(err => {
				console.log(err)
			})
		},
		clearFilters(getList) {
			this.filters = {
				page: 1,
				search_term: '',
				shopping_cart_type_id: '',
				shopping_cart_type_code: '',
				start_date: '',
				end_date: '',
				customer_carts: false,
				empty_carts: true,
				sort_descending: false,
				do_export: false
			}
			if (getList) {
				this.getList(1)
			}
		},
		toggleFilter(which) {
			switch (which) {
				case "shopping_cart_type":
					break;
				default:
					this.filters[which] = !this.filters[which]
					break;
			}
			this.filters.page = 1
			this.getList()
		},
		getList(page) {
			if (this.loading) { return }
			if (page) {
				this.filters.page = page
			}
			this.message = 'Loading carts...'
			this.messageStatus = 'waiting'
			this.showDetail = false
			this.cartDetail = {}
			this.loading = true
			this.apiCall({ destination: 'get_list_shopping_carts', data: this.filters }).then(obj => {
				this.loading = false
				this.clientId = obj.client_id
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.message = ''
					}, 1000)
				} else {
					this.message = ''
					this.list = obj.list
					this.paging = obj.paging
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItemRoute(shoppingCartId) {
			this.$router.push('/orders-sales/carts/' + shoppingCartId)
		},
		getItem(shoppingCartId) {
			this.loading = true
			this.apiCall({ destination: 'get_item_shopping_cart_detail', data: { get_shopping_cart_id: shoppingCartId } }).then(obj => {
				this.loading = false
				if (obj.status == 'success') {
					this.cartDetail = obj.shopping_cart
					//this.updateCustomRouteName('Detail');
					this.showDetail = true
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		createCart() {
			this.cartDetail = {
				shopping_cart_type_id: '',
				contact_id: '',
				billing: {}
			}
			this.customerSearchMessage = ''
			this.customerList = []
			this.showCreateModal = true
		},
		searchCustomers() {
			if (this.searching || !this.cartDetail.billing.full_name) { return }
			this.searching = true
			this.apiCall({ destination: 'get_list_contacts', data: { search_term: this.cartDetail.billing.full_name } }).then(obj => {
				this.searching = false
				if (obj.list && obj.list.customers) {
					if (obj.list.customers.length == 0) {
						this.customerSearchMessage = 'No customers found!'
					} else if (obj.list.customers.length == 1) {
						this.cartDetail.contact_id = obj.list.customers[0]['contact_id']
						this.cartDetail.billing = obj.list.customers[0]
						this.customerSearchMessage = 'Selected customer...'
					} else {
						this.customerSearchMessage = 'Multiple customers found...'
						this.customerList = obj.list.customers
					}
				} else {
					this.customerSearchMessage = 'Error searching customers'
				}
				this.customerList
			}).catch(err => {
				console.log(err)
			})
		},
		setEditedBilling(contact) {
			this.editedBilling = contact
		},
		newCustomer() {
			this.cartDetail.billing = {
				client_id: this.clientId
			}
			this.editedBilling = {
				client_id: this.clientId,
				notes: [{ note: '', priority: '' }]
			}
			this.showContactModal = true
		},
		assignCustomer(contact) {
			this.cartDetail.contact_id = contact.contact_id
			if (!contact.full_name) {
				contact.full_name = this.$fullName(contact)
			}
			this.cartDetail.billing = contact
			this.customerList = []
			this.customerSearchMessage = 'Selected customer...'
		},
		saveCustomer() {
			if (!this.customerInfoComlete) { return }
			this.apiCall({ destination: 'save_item_contact', data: this.editedBilling }).then(obj => {
				if (obj.status == 'success') {
					this.editedBilling.contact_id = obj.contact_id
					this.assignCustomer(this.editedBilling)
					this.showContactModal = false
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveCart() {
			if (this.saving || !this.cartDetail.contact_id) { return }
			this.saving = true
			this.apiCall({ destination: 'save_item_shopping_cart_detail', data: { save_shopping_cart_id: this.shopping_cart_id, cart_detail: this.cartDetail } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.customerSearchMessage = ''
					this.customerList = []
					this.showCreateModal = false
					if (this.shopping_cart_id != obj.saved_shopping_cart_id) {
						this.$router.push('/orders-sales/carts/' + obj.saved_shopping_cart_id)
					} else {
						this.getItem(obj.saved_shopping_cart_id)
					}
				} else {
					this.saveMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		printCart(which) {
			this.cartDetail.exclude_cost = (which == 'customer')
			this.showPrint = true
			setTimeout(() => {
				window.print()
				this.showPrint = false
			}, 500)
		},
		deleteCart() {
			if (this.saving) { return }
        	this.apiCall({ destination: 'delete_user_shopping_cart', data: { delete_shopping_cart_id: this.cartDetail.shopping_cart_id } }).then(obj => {
            	this.saving = false
            	if (obj.status == 'error') {
	            	this.saveMessage = obj.message
            	} else {
					this.confirmDeleteCart = false
					this.backToList()
            	}
        	})
		},
		backToList() {
			//this.updateCustomRouteName();
			this.$router.push('/orders-sales/carts')
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.order_id = ''
			this.filters.do_export = true
			this.getList(1)
		},
		openTermsModal() {
			this.apiCall({ destination: 'get_item_preference', data: { preference_code: 'GUNSMITHING_TERMS' } }).then(obj => {
				this.terms = obj.GUNSMITHING_TERMS
				this.showTermsModal = true
			}).catch(err => {
				console.log(err)
			})
		},
		saveTerms() {
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'GUNSMITHING_TERMS', value: this.terms } }).then(obj => {
				this.showTermsModal = false
			}).catch(err => {
				console.log(err)
			})
		},
		goToOrders() {
			if (!this.filters.shopping_cart_type_id) { return }
			let cartTypeCode = this.shoppingCartTypes.find(type => type.shopping_cart_type_id == this.filters.shopping_cart_type_id).shopping_cart_type_code
			console.log(cartTypeCode)
			this.$router.push('/orders-sales/orders?source=' + cartTypeCode.toLowerCase())
		}
	}
}
</script>

<style lang="scss" scoped>
textarea.terms-textarea {
    width: 33em;
    min-height: 16em;
}
</style>
